import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Heart, Search, Download, LogOut, X, Eye } from 'lucide-react';
import { useNoIndex } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['New', 'Under Review', 'Matched', 'Contact Shared', 'Connected', 'Closed', 'Refunded'];
const STATUS_CLS = { 'New': 'st-pending', 'Under Review': 'st-expired', 'Matched': 'st-matched', 'Contact Shared': 'st-matched', 'Connected': 'st-matched', 'Closed': 'st-muted', 'Refunded': 'st-refunded' };

const FIELD_LABELS = {
  name: 'Full Name', gender: 'Gender', age: 'Age', country: 'Country', city: 'City', email: 'Email', phone: 'Contact / WhatsApp',
  occupation: 'Occupation', education: 'Education', instagram: 'Instagram', photo: 'Photo', personality: 'Personality',
  freeTime: 'Free Time', freeTimeOther: 'Free Time (Other)', funThing: 'Fun / Interesting Thing', about: 'About', channel: 'Contact Channel',
  looking: 'Looking For', agePref: 'Age Preference', connectPlatform: 'Connect On', connectPlatformOther: 'Connect On (Other)', socialId: 'Social ID',
  lookVsNature: 'Looks vs Nature', priority: 'Priority', partnerLike: 'Wants in Partner', dealbreakers: 'Dealbreakers',
  destiny: 'Believes in Destiny', loveAtFirstSight: 'Love at First Sight', color: 'Favourite Colours', pizza: 'Pizza Topping',
  idealDate: 'Ideal Date', idealDateOther: 'Ideal Date (Other)', movieType: 'Movies', movieTypeOther: 'Movies (Other)',
  chronotype: 'Morning / Night', wouldWork: 'Would Still Work',
  whenHappy: 'When Happy', whenHappyOther: 'When Happy (Other)', whenSad: 'When Sad', whenSadOther: 'When Sad (Other)',
  shareFeelings: 'Shares Feelings', cuisine: 'Cuisines', cuisineOther: 'Cuisines (Other)', foodTaste: 'Food Taste',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const refundDeadline = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  d.setDate(d.getDate() + 30);
  return d;
};
const val = (v) => (Array.isArray(v) ? (v.length ? v.join(', ') : '—') : (v || '—'));

const formatDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  return String(detail);
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.user?.role !== 'admin') {
        setError('This account does not have admin access.');
      } else {
        localStorage.setItem('krysha_admin_token', data.token);
        onLogin(data.user);
      }
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="admin-login-wrap">
      <form className="admin-login" onSubmit={submit} data-testid="admin-login-form">
        <div className="al-logo"><Heart size={26} fill="var(--red)" color="var(--red)" /> Krysha</div>
        <h2>Admin Dashboard</h2>
        <p>Sign in with your Krysha team account.</p>
        <label>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" required data-testid="admin-email-input" />
        <label>Password</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="admin-password-input" />
        {error && <div className="al-error" data-testid="admin-login-error">{error}</div>}
        <button className="btn btn-red" type="submit" disabled={busy} data-testid="admin-login-submit">{busy ? 'Signing in…' : 'Sign In'}</button>
      </form>
    </div>
  );
};

const DetailModal = ({ sub, onClose, onSave }) => {
  const [notes, setNotes] = useState(sub.admin_notes || '');
  const [reviewed, setReviewed] = useState(!!sub.reviewed);
  const [suggested, setSuggested] = useState(sub.suggested_match || '');

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="submission-detail-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{sub.data?.name || 'Submission'} {sub.krysha_id && <span style={{ font: '600 12px "IBM Plex Mono"', color: 'var(--muted)' }}>{sub.krysha_id}</span>}</h3>
            <span className="modal-sub">{sub.plan_name} · {sub.plan_price} · Submitted {fmtDate(sub.submitted_at)} · {sub.guest ? 'Guest' : 'Registered'} · Submission ID {sub.id?.slice(0, 8).toUpperCase()}</span>
          </div>
          <button className="modal-x" onClick={onClose} data-testid="detail-modal-close"><X size={18} /></button>
        </div>
        <div className="modal-admin-panel">
          <label className="map-check">
            <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} data-testid="reviewed-checkbox" /> Profile reviewed
          </label>
          <input className="map-input" value={suggested} onChange={(e) => setSuggested(e.target.value)} placeholder="Suggested match (Krysha ID / name)" data-testid="suggested-match-input" />
          <input className="map-input" style={{ flex: 2 }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Match notes…" data-testid="admin-notes-input" />
          <button className="btn btn-red btn-sm" onClick={() => onSave(sub, { admin_notes: notes, reviewed, suggested_match: suggested })} data-testid="save-admin-fields-btn">Save</button>
        </div>
        <div className="modal-body">
          {Object.entries(FIELD_LABELS).map(([key, label]) => (
            <div className="detail-item" key={key}>
              <span className="dlabel">{label}</span>
              <span className="dval">{val(sub.data?.[key])}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function AdminPage() {
  useNoIndex();
  const [auth, setAuth] = useState(null);
  const [view, setView] = useState('submissions');
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [userQ, setUserQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [fPlan, setFPlan] = useState('');
  const [fGender, setFGender] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [detail, setDetail] = useState(null);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('krysha_admin_token')}` });

  useEffect(() => {
    const token = localStorage.getItem('krysha_admin_token');
    if (!token) { setAuth(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => (data.role === 'admin' ? setAuth(data) : setAuth(false)))
      .catch(() => { localStorage.removeItem('krysha_admin_token'); setAuth(false); });
  }, []);

  const loadAll = () => {
    setLoading(true);
    axios.get(`${API}/admin/submissions`, { headers: authHeader() }).then(({ data }) => setSubs(data)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/admin/stats`, { headers: authHeader() }).then(({ data }) => setStats(data)).catch(() => {});
  };

  useEffect(() => { if (auth && auth !== false) loadAll(); }, [auth]); // eslint-disable-line

  const searchUsers = (query) => {
    axios.get(`${API}/admin/users`, { params: { q: query }, headers: authHeader() }).then(({ data }) => setUsersList(data)).catch(() => {});
  };
  useEffect(() => { if (auth && auth !== false && view === 'users') searchUsers(userQ); }, [view, auth]); // eslint-disable-line

  const logout = () => { localStorage.removeItem('krysha_admin_token'); setAuth(false); setSubs([]); };

  const patchSub = async (sub, updates) => {
    try {
      const { data } = await axios.patch(`${API}/admin/submissions/${sub.id}`, updates, { headers: authHeader() });
      setSubs((all) => all.map((s) => (s.id === sub.id ? data : s)));
      if (detail?.id === sub.id) setDetail(data);
    } catch { loadAll(); }
  };

  const filtered = useMemo(() => subs.filter((s) => {
    const d = s.data || {};
    if (fPlan && s.plan_id !== fPlan) return false;
    if (fGender && d.gender !== fGender) return false;
    if (fStatus && s.match_status !== fStatus) return false;
    if (q) {
      const hay = `${d.name || ''} ${d.email || ''} ${d.city || ''} ${d.phone || ''} ${s.krysha_id || ''}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [subs, q, fPlan, fGender, fStatus]);

  const exportCSV = () => {
    const cols = ['Submission ID', 'Krysha ID', 'Name', 'Gender', 'Age', 'Country', 'City', 'Email', 'WhatsApp', 'Occupation', 'Education', 'Instagram', 'Personality', 'Free Time', 'About Yourself', 'Fun Thing', 'Destiny', 'Love At First Sight', 'Ideal Date', 'Movies', 'Chronotype', 'When Happy', 'When Sad', 'Cuisines', 'Food Taste', 'Looking For', 'Age Preference', 'Connect On', 'Social ID', 'Looks vs Nature', 'Priority', 'Wants in Partner', 'Dealbreakers', 'Plan', 'Payment Status', 'Submitted', 'Refund Deadline', 'Match Status', 'Reviewed', 'Suggested Match', 'Admin Notes'];
    const esc = (x) => `"${String(Array.isArray(x) ? x.join('; ') : (x ?? '')).replace(/"/g, '""')}"`;
    const rows = filtered.map((s) => {
      const d = s.data || {};
      return [s.id, s.krysha_id || 'Guest', d.name, d.gender, d.age, d.country, d.city, d.email, d.phone, d.occupation, d.education, d.instagram, d.personality, d.freeTime, d.about, d.funThing, d.destiny, d.loveAtFirstSight, d.idealDate, d.movieType, d.chronotype, d.whenHappy, d.whenSad, d.cuisine, d.foodTaste, d.looking, d.agePref, d.connectPlatform, d.socialId, d.lookVsNature, d.priority, d.partnerLike, d.dealbreakers, `${s.plan_name} (${s.plan_price})`, s.payment_status, fmtDate(s.submitted_at), fmtDate(refundDeadline(s.submitted_at)), s.match_status, s.reviewed ? 'Yes' : 'No', s.suggested_match, s.admin_notes].map(esc).join(',');
    });
    const csv = [cols.map(esc).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `krysha-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (auth === null) return <div className="admin-login-wrap"><p style={{ color: 'var(--muted)' }}>Loading…</p></div>;
  if (auth === false) return <Login onLogin={setAuth} />;

  const plans = [...new Set(subs.map((s) => s.plan_id))];

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <header className="admin-head">
        <div className="container admin-head-in">
          <div className="al-logo"><Heart size={22} fill="var(--red)" color="var(--red)" /> Krysha <span className="admin-tag">ADMIN</span></div>
          <div className="admin-head-right">
            <span className="admin-user">{auth.email}</span>
            <button className="btn btn-ghost btn-sm" onClick={logout} data-testid="admin-logout-btn"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="container">
        {stats && (
          <div className="stats-row" data-testid="admin-stats">
            <div className="stat-card"><b>{stats.total_submissions}</b><span>Total Forms</span></div>
            <div className="stat-card"><b>{stats.guest_submissions}</b><span>Guest Submissions</span></div>
            <div className="stat-card"><b>{stats.registered_users}</b><span>Registered Users</span></div>
            <div className="stat-card"><b>{stats.pending_submissions}</b><span>Pending</span></div>
            <div className="stat-card"><b>{stats.completed_submissions}</b><span>Completed</span></div>
          </div>
        )}

        <div className="dash-tabs" style={{ marginBottom: 16 }}>
          <button className={`dash-tab ${view === 'submissions' ? 'on' : ''}`} onClick={() => setView('submissions')} data-testid="admin-tab-submissions">Form Submissions</button>
          <button className={`dash-tab ${view === 'users' ? 'on' : ''}`} onClick={() => setView('users')} data-testid="admin-tab-users">User Management</button>
        </div>

        {view === 'submissions' && (<>
          <div className="admin-toolbar">
            <div className="admin-search">
              <Search size={15} />
              <input placeholder="Search name, email, city, phone, Krysha ID…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" />
            </div>
            <select value={fPlan} onChange={(e) => setFPlan(e.target.value)} data-testid="admin-filter-plan">
              <option value="">All Plans</option>
              {plans.map((p) => <option key={p} value={p}>₹{p}</option>)}
            </select>
            <select value={fGender} onChange={(e) => setFGender(e.target.value)} data-testid="admin-filter-gender">
              <option value="">All Genders</option>
              <option>Woman</option><option>Man</option><option>Prefer not to say</option>
            </select>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="admin-filter-status">
              <option value="">All Statuses</option>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <button className="btn btn-red btn-sm" onClick={exportCSV} data-testid="admin-export-csv-btn"><Download size={15} /> Export CSV</button>
          </div>

          <div className="admin-count" data-testid="admin-submission-count">{filtered.length} submission{filtered.length === 1 ? '' : 's'}</div>

          <div className="admin-table-wrap">
            <table className="admin-table" data-testid="admin-submissions-table">
              <thead>
                <tr>
                  <th>Sub ID</th><th>Krysha ID</th><th>Name</th><th>Email</th><th>Mobile</th><th>City</th>
                  <th>Plan</th><th>Payment</th><th>Submitted</th><th>Refund By</th><th>Match Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={12} className="admin-empty">Loading submissions…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={12} className="admin-empty">No submissions yet.</td></tr>
                )}
                {!loading && filtered.map((s) => (
                  <tr key={s.id} data-testid={`submission-row-${s.id}`}>
                    <td style={{ fontFamily: 'IBM Plex Mono', fontSize: 11 }}>{s.id.slice(0, 8).toUpperCase()}</td>
                    <td>{s.krysha_id || <span className="badge-muted pay-badge">Guest</span>}</td>
                    <td><b>{s.data?.name || '—'}</b></td>
                    <td>{s.data?.email || '—'}</td>
                    <td>{s.data?.phone || '—'}</td>
                    <td>{s.data?.city || '—'}</td>
                    <td>{s.plan_name}<br /><small>{s.plan_price}</small></td>
                    <td><span className="pay-badge">{s.payment_status}</span></td>
                    <td>{fmtDate(s.submitted_at)}</td>
                    <td>{fmtDate(refundDeadline(s.submitted_at))}</td>
                    <td>
                      <select
                        className={`status-select ${STATUS_CLS[s.match_status] || ''}`}
                        value={s.match_status}
                        onChange={(e) => patchSub(s, { match_status: e.target.value })}
                        data-testid={`status-select-${s.id}`}
                      >
                        {STATUSES.map((st) => <option key={st}>{st}</option>)}
                      </select>
                    </td>
                    <td>
                      <button className="view-btn" onClick={() => setDetail(s)} data-testid={`view-submission-${s.id}`}><Eye size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>)}

        {view === 'users' && (<>
          <div className="admin-toolbar">
            <div className="admin-search">
              <Search size={15} />
              <input placeholder="Search by Krysha ID, name, email or mobile…" value={userQ} onChange={(e) => { setUserQ(e.target.value); searchUsers(e.target.value); }} data-testid="admin-user-search-input" />
            </div>
          </div>
          <div className="admin-count">{usersList.length} registered user{usersList.length === 1 ? '' : 's'}</div>
          <div className="admin-table-wrap">
            <table className="admin-table" data-testid="admin-users-table">
              <thead>
                <tr><th>Krysha ID</th><th>Name</th><th>Email</th><th>Mobile</th><th>Status</th><th>Joined</th></tr>
              </thead>
              <tbody>
                {usersList.length === 0 && <tr><td colSpan={6} className="admin-empty">No registered users found.</td></tr>}
                {usersList.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.krysha_id}`}>
                    <td style={{ fontFamily: 'IBM Plex Mono', fontSize: 12 }}>{u.krysha_id}</td>
                    <td><b>{u.name}</b></td>
                    <td>{u.email}</td>
                    <td>{u.phone || '—'}</td>
                    <td><span className="pay-badge">{u.status}</span></td>
                    <td>{fmtDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>)}
      </div>

      {detail && <DetailModal sub={detail} onClose={() => setDetail(null)} onSave={patchSub} />}
    </div>
  );
}
